/**
 * Incidents API Service
 * 
 * Provides access to incident management
 */

import { mapSafetyEpisodeObservation } from '../domain/incidents/observationEpisodeRow';
import { api } from './api';
import type { BuyerLiveItem, BuyerLiveResponse } from '../domain/incidents/liveAlertsReconciliation';

export type IncidentSeverity = 'low' | 'medium' | 'high' | 'critical';
// List filter param (server accepts: open|closed or CSV of workflow statuses)
export type IncidentStatus = 'open' | 'closed';
export type IncidentWorkflowStatus = 'OPEN' | 'ACKED' | 'IN_REVIEW' | 'CLOSED' | 'SUPPRESSED';
export type IncidentOpsState = 'new' | 'acknowledged' | 'in_progress' | 'resolved' | 'closed';
export type IncidentEvidenceStatus = 'PENDING' | 'AVAILABLE' | 'FAILED';
export type IncidentCloseReason = 'TRUE_POSITIVE' | 'FALSE_POSITIVE' | 'TEST' | 'DUPLICATE' | 'NOT_ACTIONABLE';
export type IncidentReviewOutcome =
  | 'true_event'
  | 'false_positive'
  | 'demo_or_test_activity'
  | 'duplicate'
  | 'expected_activity'
  | 'insufficient_evidence'
  | 'not_actionable';

export interface CanonicalLabels {
  incident_label: string;
  incident_type_key?: string | null;
  workflow_status_label: string;
  edge_state_label: string;
  zone_label?: string | null;
  camera_label?: string | null;
  device_label?: string | null;
  policy_label?: string | null;
  severity_label?: string | null;
}

export interface ProofScopeSummary {
  proof_scope_id: string;
  scope_type: 'incident_window' | string;
  org_id?: string | null;
  site_id?: string | null;
  device_id?: string | null;
  zone_id?: string | null;
  camera_ids?: string[];
  policy_ids?: string[];
  policy_type?: string | null;
  start_ts?: string | null;
  end_ts?: string | null;
  anchor_incident_id?: string | null;
  included_incident_cases?: number | null;
}

export interface ReviewSummary {
  workflow_status?: string | null;
  close_reason?: string | null;
  operator_note?: string | null;
  review_outcome?: IncidentReviewOutcome | string | null;
  review_outcome_source?: 'explicit' | 'close_reason' | 'workflow_status' | 'unknown' | string;
}

export interface OwnershipSummary {
  rule_owner?: string | null;
  followup_owner?: string | null;
  assignee?: string | null;
  assignment_team?: string | null;
  assigned_at?: string | null;
  assigned_by?: string | null;
  resolved_by?: string | null;
  resolved_at?: string | null;
  has_owner?: boolean;
}

export interface OutcomeMeasurementSummary {
  baseline_available?: boolean;
  measurement_available?: boolean;
  readiness_state?: string | null;
  state_label?: string | null;
  impact_waived?: boolean;
  waived_by?: string | null;
  waived_at?: string | null;
  waiver_reason?: string | null;
  waiver_scope?: string | null;
  waiver_permission_level?: string | null;
  audit_event_id?: string | null;
}

export interface IncidentMeasurementPatchInput {
  baseline_count?: number | null;
  follow_up_count?: number | null;
  measured_at?: string | null;
  outcome_status?: string | null;
  note?: string | null;
  coverage_review?: {
    reviewed?: boolean | null;
    review_status?: string | null;
    evidence_reference?: string | null;
  } | null;
  zone_controls_confirmation?: {
    controls_present?: boolean | null;
    controls_functioning?: boolean | null;
    evidence_reference?: string | null;
  } | null;
  impact_waived?: boolean | null;
  waiver_reason?: string | null;
  waiver_scope?: string | null;
  waiver_permission_level?: string | null;
}

export interface IncidentCorrectiveActionPatchInput {
  action_type?: string | null;
  priority?: string | null;
  due_at?: string | null;
  safety_control_review?: {
    case_decision?: 'accepted' | 'rejected' | 'deferred' | 'already_managed' | 'excluded' | string | null;
    control_objective?: string | null;
    control_level?: string | null;
    potential_control_gap?: string | null;
    field_verification_question?: string | null;
    verification_status?: string | null;
    closure_requirement?: string | null;
    effectiveness_boundary?: string | null;
    follow_up_observation?: string | null;
  } | null;
}

export interface CorrectiveActionSummary {
  work_order_id?: string | number | null;
  work_order_state?: string | null;
  work_order_url?: string | null;
  has_work_order?: boolean;
  action_state?: string | null;
  action_type?: string | null;
  priority?: string | null;
  due_at?: string | null;
  due_date?: string | null;
}

export interface ActionSnapshotSummary {
  owner?: string | null;
  has_owner?: boolean;
  work_order_id?: string | number | null;
  work_order_state?: string | null;
  has_work_order?: boolean;
  next_review_at?: string | null;
  action_state?: string | null;
}

export interface EscalationSummary {
  state?: string | null;
  overdue?: boolean;
  level?: number | null;
  reasons?: string[] | null;
  next_review_at?: string | null;
  last_escalated_at?: string | null;
  needs_attention?: boolean;
}

export interface DiagnosticImpactSummary {
  degraded?: boolean;
  degraded_reason?: string | null;
  evidence_limited_by_diagnostics?: boolean;
}

export interface TraceabilitySummary {
  source_record_id?: string | null;
  source_schema_version?: string | null;
  odoo_ticket_id?: string | null;
  odoo_ticket_url?: string | null;
  policy_id?: string | null;
  policy_type?: string | null;
  policy_version?: number | null;
  proof_scope_id?: string | null;
}

export interface SafetyCase {
  schema_version?: string;
  incident_id: string;
  source_record_id?: string | null;
  scope?: Record<string, any>;
  labels?: CanonicalLabels;
  lifecycle?: Record<string, any>;
  evidence?: Record<string, any>;
  proof_scope?: ProofScopeSummary;
  review?: ReviewSummary;
  ownership?: OwnershipSummary;
  escalation?: EscalationSummary;
  corrective_action?: CorrectiveActionSummary;
  action_snapshot?: ActionSnapshotSummary;
  outcome_measurement?: OutcomeMeasurementSummary;
  diagnostics?: DiagnosticImpactSummary;
  traceability?: TraceabilitySummary;
}

export interface EvidenceAttachment {
  filename: string;
  type: 'snapshot' | 'video' | 'frame' | string;
  size?: number;
  local_path?: string;
  odoo_attachment_id?: number;
  odoo_incident_id?: number;
  uploaded_at?: string;
  metadata?: Record<string, any> | any;
}

export interface EvidenceInfo {
  attachments?: EvidenceAttachment[];
  latest?: {
    snapshot?: EvidenceAttachment;
    video?: EvidenceAttachment;
  };
}

export interface IncidentReviewMediaRef {
  filename?: string | null;
  type?: string | null;
  confidence?: number | null;
  uploaded_at?: string | null;
  captured_at?: string | null;
  condition_started_ts_ms?: number | null;
  threshold_crossed_ts_ms?: number | null;
  capture_requested_ts_ms?: number | null;
  evidence_frame_target_ts_ms?: number | null;
  evidence_ts_ms?: number | null;
  event_ts_ms?: number | null;
  evidence_delta_ms?: number | null;
  review_role?: 'source_truth' | 'reviewer_aid' | 'unclassified' | string | null;
  evidence_class?: string | null;
  source?: string | null;
  snapshot_overlay?: string | null;
  display_label?: string | null;
  bbox_status_label?: string | null;
  aligned_person_bbox?: boolean | null;
  person_bbox_drawn?: boolean | null;
  trigger_subject_key?: string | null;
  subject_identity_retained?: boolean | null;
  incident_subject_label?: string | null;
  trigger_person_verification_status?: string | null;
  trigger_person_verification_label?: string | null;
  uri?: string | null;
}

export interface IncidentReviewMedia {
  schema_version?: string | null;
  source_truth?: {
    snapshot?: IncidentReviewMediaRef | null;
    clip?: IncidentReviewMediaRef | null;
  } | null;
  reviewer_aids?: {
    snapshot?: IncidentReviewMediaRef | null;
    clip?: IncidentReviewMediaRef | null;
  } | null;
  unclassified?: {
    snapshot?: IncidentReviewMediaRef | null;
    clip?: IncidentReviewMediaRef | null;
  } | null;
  primary_snapshot?: IncidentReviewMediaRef | null;
  secondary_snapshot?: IncidentReviewMediaRef | null;
  notes?: string[] | null;
}

// Hosted workflow writes can sit behind row locks for 60s+ under load.
// Avoid surfacing a client timeout while the cloud is still applying the change.
const INCIDENT_WORKFLOW_WRITE_TIMEOUT_MS = 180000;

export interface IncidentTiming {
  schema_version?: string | null;
  condition_started_ts_ms?: number | null;
  threshold_crossed_ts_ms?: number | null;
  capture_requested_ts_ms?: number | null;
  evidence_frame_target_ts_ms?: number | null;
  clip_start_ts_ms?: number | null;
  clip_end_ts_ms?: number | null;
  clip_event_covered?: boolean | null;
  clip_event_gap_ms?: number | null;
  clip_event_grace_ms?: number | null;
  clip_seconds?: number | null;
  clip_post_ms?: number | null;
  clip_triggered_recording?: boolean | null;
  clip_source?: string | null;
  event_ts_ms?: number | null;
  event_started_ts_ms?: number | null;
  alert_triggered_ts_ms?: number | null;
  evidence_anchor_ts_ms?: number | null;
  evidence_ts_ms?: number | null;
  evidence_delta_ms?: number | null;
  capture_requested_at?: string | null;
  captured_at?: string | null;
  capture_latency_ms?: number | null;
  capture_total_latency_ms?: number | null;
  timing_source?: string | null;
  threshold_inferred?: boolean | null;
  capture_anchor_policy?: string | null;
}

export interface RoutingDestination {
  success?: boolean;
  enabled?: boolean;
  error?: string | null;
  ticket_id?: number | string;
  timestamp?: string;
}

export interface RoutingStatus {
  dashboard?: RoutingDestination;
  odoo?: RoutingDestination;
  webhook?: RoutingDestination;
}

/** Per-type status for policy outbound actions (envelope_metadata.actions at incident create). */
export type PolicyNotificationRouteState =
  | 'delivered'
  | 'failed'
  | 'pending'
  | 'disabled'
  /** Policy snapshot exists but this action type was not on the policy. */
  | 'not_listed'
  /** No actions[] on the incident (older ingest or missing policy_id path). */
  | 'no_snapshot';

export interface PolicyNotificationRoute {
  action_type: 'email.notify' | 'sms.notify' | 'genetec.notify' | 'webhook.call';
  label: string;
  state: PolicyNotificationRouteState;
  error?: string | null;
}

const POLICY_NOTIFICATION_DEFS: ReadonlyArray<{
  action_type: PolicyNotificationRoute['action_type'];
  label: string;
}> = [
  { action_type: 'email.notify', label: 'Email' },
  { action_type: 'sms.notify', label: 'SMS' },
  { action_type: 'genetec.notify', label: 'Genetec' },
  { action_type: 'webhook.call', label: 'Webhook (policy)' },
];

/** Derive UI rows from cloud-stored envelope actions (see incidents API incident.create). */
export function derivePolicyNotificationRoutes(envelope_metadata: unknown): PolicyNotificationRoute[] {
  const env = envelope_metadata && typeof envelope_metadata === 'object' ? (envelope_metadata as Record<string, unknown>) : {};
  const actions = env.actions;
  const hasSnapshot = Array.isArray(actions);
  if (!hasSnapshot) {
    return POLICY_NOTIFICATION_DEFS.map((d) => ({
      action_type: d.action_type,
      label: d.label,
      state: 'no_snapshot' as const,
      error: null,
    }));
  }
  const list = actions as unknown[];
  return POLICY_NOTIFICATION_DEFS.map((d) => {
    const a = list.find((x) => typeof x === 'object' && x && String((x as Record<string, unknown>).type || '').trim() === d.action_type) as
      | Record<string, unknown>
      | undefined;
    if (!a) {
      return { action_type: d.action_type, label: d.label, state: 'not_listed' as const, error: null };
    }
    if (a.enabled === false) {
      return { action_type: d.action_type, label: d.label, state: 'disabled' as const, error: null };
    }
    const st = String(a.status || '').trim().toLowerCase();
    if (st === 'succeeded') {
      return { action_type: d.action_type, label: d.label, state: 'delivered' as const, error: null };
    }
    if (st === 'failed') {
      const err = a.last_error != null ? String(a.last_error) : null;
      return { action_type: d.action_type, label: d.label, state: 'failed' as const, error: err };
    }
    if (st === 'pending') {
      return { action_type: d.action_type, label: d.label, state: 'pending' as const, error: null };
    }
    return { action_type: d.action_type, label: d.label, state: 'pending' as const, error: null };
  });
}

export interface IncidentComment {
  id: string;
  incident_id: string;
  comment_text: string;
  created_by?: string | null;
  created_at?: string | null;
}

export interface IncidentThresholdAdjustmentResponse {
  incident?: Incident;
  policy?: {
    policy_id: string;
    site_id?: string;
    org_id?: string;
    policy_type?: string;
    version?: number;
    updated_at?: string | null;
    updated_fields?: string[];
    config?: Record<string, any>;
    timing?: Record<string, any>;
  };
}

export interface IncidentSafetyEpisode {
  episode_id: string;
  incident_id?: string | null;
  camera_id?: string | null;
  zone_id?: string | null;
  policy_id?: string | null;
  incident_type?: string | null;
  family?: string | null;
  shape?: string | null;
  started_at?: string | null;
  qualified_at?: string | null;
  last_seen_at?: string | null;
  ended_at?: string | null;
  local_track_id?: string | null;
  global_subject_id?: string | null;
  status?: string | null;
  boundary_reason?: string | null;
  evidence_id?: string | null;
  snapshot_id?: string | null;
  clip_id?: string | null;
  clip_offset_ms?: number | null;
  source_frame_id?: string | null;
  evidence_status?:
    | 'indexed'
    | 'media_pending'
    | 'outside_clip_window'
    | 'media_unavailable'
    | 'legacy_unindexed'
    | null;
  revision?: number | null;
  last_event_type?: string | null;
  metadata?: Record<string, any> | null;
  forklift_present?: boolean | null;
  nearby_forklift_track_ids?: string[] | null;
  forklift_co_presence_observed_at?: string | null;
  confidence?: number | null;
}

export interface IncidentSafetyEpisodesResponse {
  incident_id: string;
  available: boolean;
  episodes: IncidentSafetyEpisode[];
  error?: string | null;
}

export interface IncidentActivityItem {
  ts: string;
  kind: string;
  title: string;
  subtitle?: string | null;
  detail?: string | null;
  activity_type?: string | null;
  workflow_fields?: string[] | null;
  workflow_facts?: Array<{ label: string; value: string }> | null;
  source_context?: string | null;
  non_production_mode?: boolean;
  real_world_outcome_claimed?: boolean;
  risk_reduction_claim_allowed?: boolean;
  review_outcome?: string | null;
  risk_disposition?: string | null;
  risk_disposition_label?: string | null;
  review_intelligence?: {
    learning_signal?: string | null;
    risk_disposition?: string | null;
    counts_toward_customer_risk?: boolean;
    counts_toward_recurrence_signal?: boolean;
    production_claim_allowed?: boolean;
    proof_strength?: string | null;
  } | null;
}

export interface Incident {
  id: string;
  incident_id: string;
  title: string;
  description: string;
  severity: IncidentSeverity;
  // Gate 3: explicit workflow status (SoR)
  status: IncidentWorkflowStatus;
  ops_state?: IncidentOpsState;
  assignee?: string | null;
  assignment_team?: string | null;
  assigned_at?: string | null;
  assigned_by?: string | null;
  pattern_owner?: string | null;
  pattern_owner_team?: string | null;
  pattern_assigned_at?: string | null;
  pattern_assigned_by?: string | null;
  pattern_key?: string | null;
  canonical_pattern_owner?: string | null;
  canonical_pattern_owner_team?: string | null;
  canonical_pattern_assigned_at?: string | null;
  canonical_pattern_assigned_by?: string | null;
  pattern_owner_display?: string | null;
  followup_owner?: string | null;
  followup_team?: string | null;
  followup_next_review_at?: string | null;
  followup_updated_at?: string | null;
  followup_updated_by?: string | null;
  followup_owner_display?: string | null;
  sla_target_at?: string | null;
  ops_overdue?: boolean;
  ops_escalation_level?: number;
  ops_escalation_reasons?: string[];
  last_escalated_at?: string | null;
  resolved_at?: string | null;
  resolved_by?: string | null;
  version: number;
  assigned_to: string | null;
  created_at: string;
  updated_at: string;
  zone_id?: string;
  device_id?: string;
  camera_id?: string;
  incident_type?: string;
  org_id?: string;
  site_id?: string;
  /** Immutable event-time location.snapshot.v1 when present. */
  location_snapshot?: Record<string, any> | null;
  ts_start?: string;
  ts_end?: string;
  close_reason?: IncidentCloseReason | null;
  close_note?: string | null;
  review_outcome?: IncidentReviewOutcome | null;
  review_outcome_source?: 'explicit' | 'close_reason' | 'workflow_status' | 'unknown' | string | null;
  operator_tags?: any;
  operator_note?: string | null;
  operator_note_updated_at?: string | null;
  operator_note_updated_by?: string | null;
  // Gate 3: cloud-derived evidence readiness
  snapshot_status?: IncidentEvidenceStatus | null;
  snapshot_uri?: string | null;
  snapshot_error?: string | null;
  clip_status?: IncidentEvidenceStatus | null;
  clip_uri?: string | null;
  clip_error?: string | null;
  meta_status?: IncidentEvidenceStatus | null;
  meta_uri?: string | null;
  meta_error?: string | null;
  evidence?: EvidenceInfo;
  /** Package G: server arbitration (preferred + additional + coverage). */
  evidence_selection?: {
    schema_version?: string;
    event_t0_ms?: number | null;
    preferred_clip?: {
      filename?: string | null;
      capture_reason?: string | null;
      producer_reported_coverage?: boolean | null;
      computed_covers_t0?: boolean | null;
      clip_start_ts_ms?: number | null;
      clip_end_ts_ms?: number | null;
      selection_reason?: string | null;
    } | null;
    preferred_snapshot?: {
      filename?: string | null;
      capture_reason?: string | null;
      selection_reason?: string | null;
    } | null;
    additional_clips?: Array<Record<string, unknown>>;
    additional_snapshots?: Array<Record<string, unknown>>;
    selection_reason?: string;
    coverage_status?: string;
    candidates?: Array<Record<string, unknown>>;
  } | null;
  evidence_readiness_policy?: string | null;
  clip_required_for_ready?: boolean | null;
  timing?: IncidentTiming | null;
  review_media?: IncidentReviewMedia | null;
  actors?: Record<string, any> | any;
  metrics?: Record<string, any> | any;
  envelope_metadata?: Record<string, any> | any;
  triggered_by?: { policy_id?: string; policy_type?: string; policy_name?: string } | any;
  policy_id?: string;
  policy_type?: string;
  /** Resolved from policies table on list (when policy_id matches). */
  policy_name?: string;
  canonical_labels?: CanonicalLabels;
  proof_scope?: ProofScopeSummary;
  safety_case?: SafetyCase;
  degraded?: boolean;
  degraded_reason?: string | null;
  // Phase 2 / Item 7: taxonomy + ownership + playbooks (cloud-computed)
  owner?: string;
  taxonomy?: {
    incident_type?: string;
    label?: string;
    category?: string;
    owner?: string;
    description?: string;
    recommended_actions?: string[];
    investigation_steps?: string[];
    signals?: Record<string, any>;
  };
  // Raw API row for debugging / JSON view
  raw?: any;
  // Odoo Integration
  odoo_ticket_id?: number;
  odoo_ticket_state?: string;
  odoo_synced_at?: string;
  odoo_ticket_url?: string;
  // Routing status
  routing_status?: RoutingStatus;
  /** Policy-defined email/SMS/Genetec from envelope_metadata.actions */
  policy_notification_routes?: PolicyNotificationRoute[];
  is_test?: boolean;
  /** Edge-reported live condition (separate from cloud workflow status). */
  edge_state?: string | null;
  edge_active_at?: string | null;
  edge_cleared_at?: string | null;
  edge_clear_reason?: string | null;
  last_edge_update_at?: string | null;
  /** Latest correlated activity time from cloud list/detail payloads (distinct from row updated_at). */
  last_seen?: string | null;
  last_seen_at?: string | null;
  /** Epic 262: explicit qualifying safety episode accounting (distinct from Epic 71 pattern groups). */
  episode_count?: number | null;
  /** Epic 263: measured observation metrics; omitted when the source cannot measure them. */
  restricted_zone_occupancy_duration_ms?: number | null;
  forklift_co_presence_entry_count?: number | null;
  active_episode_count?: number | null;
  observed_track_count?: number | null;
  /** vnext-2 qualifying observations accumulated into the episode ledger. */
  observation_count?: number | null;
  observed_pair_key_count?: number | null;
  evidence_count?: number | null;
  latest_episode_at?: string | null;
  last_observed_at?: string | null;
  safety_episodes_available?: boolean | null;
  /** Individual Observations row identity. Absent on parent incident rows. */
  episode_id?: string | null;
  observation_episode_id?: string | null;
  qualified_at?: string | null;
  parent_severity?: string | null;
  parent_incident_status?: string | null;
  episode_status?: string | null;
  evidence_status?: string | null;
  terminal_reason?: string | null;
  snapshot_id?: string | null;
  clip_id?: string | null;
  client_incident_id?: string | null;
  family?: string | null;
}

export interface OdooStatus {
  incident_id: string;
  odoo_ticket_id: number | null;
  odoo_ticket_state: string | null;
  odoo_synced_at: string | null;
  odoo_ticket_url: string | null;
}

export interface CreateIncidentRequest {
  title?: string;
  description?: string;
  severity: IncidentSeverity;
  incident_type?: string;
  zone_id?: string;
  device_id?: string;
}

export interface IncidentStats {
  total: number;
  open: number;
  closed: number;
  critical: number;
  high: number;
  resolution_rate: number;
}

export interface IncidentKpis {
  open_now: number;
  new_today: number;
  acknowledged: number;
  resolved_today: number;
  sla_breaches: number;
  as_of?: string;
  day_start_utc?: string;
  range_from?: string;
  range_to?: string;
}

export interface EdgeOutboxTypeStats {
  job_type?: string;
  total?: number;
  active_total?: number;
  oldest_pending_age_sec?: number;
  count_pending?: number;
  count_retry_wait?: number;
  count_leased?: number;
  count_sending?: number;
  count_sent?: number;
  count_failed?: number;
  count_deadletter?: number;
  count_quarantined?: number;
}

export interface EdgeOutboxSummaryResponse {
  ok?: boolean;
  error?: string;
  detail?: string;
  device_id?: string;
  ip_address?: string | null;
  mode?: Record<string, any>;
  warnings?: string[];
  outbox_all?: EdgeOutboxTypeStats;
  outbox_incident_create?: EdgeOutboxTypeStats;
  outbox_evidence_upload?: EdgeOutboxTypeStats;
  last_sent_at?: Record<string, string | null | undefined>;
  errors?: {
    failed?: Record<string, Array<{ last_error?: string | null; count?: number }>>;
    deadletter?: Record<string, Array<{ last_error?: string | null; count?: number }>>;
    quarantined?: Record<string, Array<{ last_error?: string | null; count?: number }>>;
    retry_wait?: Record<string, Array<{ last_error?: string | null; count?: number }>>;
  };
  recent?: Record<string, Array<Record<string, any>>>;
}

export interface EdgeOutboxIncidentResponse {
  ok?: boolean;
  error?: string;
  detail?: string;
  device_id?: string;
  ip_address?: string | null;
  incident_id?: string;
  limit?: number;
  counts_by_status?: Record<string, number>;
  counts_by_type?: Record<string, number>;
  jobs?: Array<Record<string, any>>;
}

export interface IncidentGroup {
  group_key: string;
  pattern_key?: string | null;
  incident_id?: string | null;
  incident_type?: string;
  severity?: IncidentSeverity | string | null;
  status?: IncidentWorkflowStatus | string | null;
  ops_state?: IncidentOpsState | string | null;
  zone_id?: string;
  camera_id?: string;
  device_id?: string;
  alert_count: number;
  first_seen?: string;
  last_seen?: string;
  started_at?: string | null;
  last_seen_at?: string | null;
  duration_seconds?: number | null;
  /** Epic 263: complete hotspot aggregates, when supplied by the group endpoint. */
  qualifying_entry_count?: number | null;
  restricted_zone_occupancy_duration_ms?: number | null;
  forklift_co_presence_entry_count?: number | null;
  summary_text?: string | null;
  recommended_action?: string | null;
  confidence_indicator?: 'high' | 'estimated' | string | null;
  evidence_count?: number | null;
  linked_detailed_record_id?: string | null;
  review_anchor_incident_id?: string | null;
  review_anchor_selection_reason?: string | null;
  latest_incident_id?: string | null;
  latest_incident_evidence_state?: string | null;
  anchor_reuse_allowed?: boolean | null;
  review_readiness?: 'ready' | 'evidence_preparing' | 'no_eligible_anchor' | 'ready_reused' | string | null;
  is_grouped?: boolean;
  group_reason?: string | null;
  recurrence_tag?: string | null;
  recent_alert_count?: number | null;
  recent_active_days?: number | null;
  pattern_source?: {
    pattern_id?: string | null;
    source_type?: 'incident_group' | string | null;
    recurrence_source?: 'incident_groups' | string | null;
    group_key?: string | null;
    representative_incident_id?: string | null;
    representative_safety_case_id?: string | null;
    selected_window_alert_count?: number | null;
    lookback_signal_count?: number | null;
    lookback_active_days?: number | null;
    pattern_state?: 'isolated' | 'emerging_pattern' | 'recurring_hotspot' | string | null;
    pattern_label?: string | null;
    pattern_summary?: string | null;
    review_priority?: 'low' | 'medium' | 'high' | string | null;
    semantic_metrics?: CustomerMetricSemantic[] | null;
  } | null;
  risk_pattern?: {
    state?: 'isolated' | 'emerging_pattern' | 'recurring_hotspot' | string | null;
    label?: string | null;
    hotspot_score?: number | null;
    recent_alert_count?: number | null;
    recent_active_days?: number | null;
    review_priority?: 'low' | 'medium' | 'high' | string | null;
    summary?: string | null;
  } | null;
  // Odoo linkage/state of the most recent incident in the group (best-effort)
  odoo_ticket_id?: number | null;
  odoo_ticket_state?: string | null;
  odoo_ticket_url?: string | null;
  /** From latest incident row in the group + policies.name join */
  policy_id?: string | null;
  policy_name?: string | null;
  canonical_pattern_owner?: string | null;
  canonical_pattern_owner_team?: string | null;
  canonical_pattern_assigned_at?: string | null;
  canonical_pattern_assigned_by?: string | null;
}

export type IncidentGroupActionName = 'close' | 'assign' | 'suppress';

export type IncidentGroupActionRequest = {
  action: IncidentGroupActionName;
  group_key: string;
  from_date: string;
  to_date: string;
  status_filter?: 'OPEN' | 'ACKED' | 'IN_REVIEW' | 'CLOSED' | 'SUPPRESSED' | 'ALL';
  apply_to?: 'open_members_in_window';
  policy_id?: string | null;
  dry_run?: boolean;
  max_members?: number;
  idempotency_key?: string;
  status?: IncidentWorkflowStatus;
  review_outcome?: IncidentReviewOutcome;
  close_reason?: IncidentCloseReason;
  close_note?: string;
  assignee?: string | null;
  assignment_team?: string | null;
  pattern_owner?: string | null;
  pattern_owner_team?: string | null;
  ops_state?: IncidentOpsState;
  suppress_duration_hours?: number;
  suppress_reason?: string;
  close_open_members?: boolean;
};

export type IncidentGroupActionResponse = {
  ok: boolean;
  group_key: string;
  action: IncidentGroupActionName;
  matched_count: number;
  affected_count: number;
  skipped_count: number;
  skipped_reasons: Array<Record<string, unknown>>;
  audit_event_ids: string[];
  representative_incident_id?: string | null;
  dry_run?: boolean;
  policy_id?: string | null;
  sample_incident_ids?: string[];
  membership_source?: 'dimension' | 'episode' | string;
  max_members?: number;
  execute_allowed?: boolean;
  execute_blocked_reason?: string;
  canonical_pattern_owner?: string | null;
  canonical_pattern_owner_team?: string | null;
  canonical_pattern_key?: string | null;
  canonical_pattern_owner_updated?: boolean;
  error?: string;
  detail?: string;
};

export interface IncidentOpsQueuesResponse {
  ok: boolean;
  org_id?: string;
  site_id?: string;
  as_of?: string;
  assignee?: string | null;
  policy?: {
    high_severity_escalation_minutes?: number;
  };
  queues?: {
    my_queue?: { count?: number; items?: Incident[] };
    my_risk_queue?: { count?: number; items?: Incident[] };
    assigned_by_me?: { count?: number; items?: Incident[] };
    risk_assigned_by_me?: { count?: number; items?: Incident[] };
    overdue?: { count?: number; items?: Incident[] };
    unassigned_high_risk?: { count?: number; items?: Incident[] };
    escalations?: { count?: number; items?: Incident[] };
  };
}

export interface IncidentGroupHeatmapRow {
  id: string;
  total_count: number;
  cells: Array<{
    hour: number;
    count: number;
    active_days?: number;
  }>;
}

export interface IncidentGroupHeatmapResponse {
  ok: boolean;
  dimension: 'zone' | 'camera';
  item_semantics: 'grouped_incident_patterns' | string;
  definition?: string;
  hours: number[];
  rows: IncidentGroupHeatmapRow[];
  filters?: {
    incident_type?: string | null;
    zone_id?: string | null;
    camera_id?: string | null;
    device_id?: string | null;
  };
  from_ts?: string;
  to_ts?: string;
}

export interface IncidentReviewOutcomeRollupItem {
  review_outcome: string;
  count: number;
}

export interface IncidentReviewOutcomeRollupSourceItem {
  review_outcome_source: string;
  count: number;
}

export interface IncidentReviewOutcomeLearningSignalItem {
  learning_signal: string;
  count: number;
}

export interface IncidentReviewOutcomeRiskDispositionItem {
  risk_disposition: string;
  count: number;
}

export interface IncidentSourceContextRollupItem {
  source_context: string;
  count: number;
}

export interface CustomerMetricSemantic {
  semantic_key: string;
  value: number;
  grain: string;
  population_key: string;
  window_kind: string;
  window_start?: string | null;
  window_end?: string | null;
  scope?: Record<string, string | null | undefined> | null;
  qualification?: Record<string, string | boolean | number | null | undefined> | null;
  review_state?: string | null;
  source?: string | null;
}

function parseCustomerMetricSemantic(metric: any): CustomerMetricSemantic {
  return {
    semantic_key: String(metric?.semantic_key || '').trim(),
    value: Number(metric?.value || 0),
    grain: String(metric?.grain || '').trim(),
    population_key: String(metric?.population_key || '').trim(),
    window_kind: String(metric?.window_kind || '').trim(),
    window_start: metric?.window_start ?? null,
    window_end: metric?.window_end ?? null,
    scope: metric?.scope && typeof metric.scope === 'object' ? metric.scope : null,
    qualification: metric?.qualification && typeof metric.qualification === 'object' ? metric.qualification : null,
    review_state: metric?.review_state ?? null,
    source: metric?.source ?? null,
  };
}

export interface IncidentReviewOutcomeRollupResponse {
  ok: boolean;
  org_id?: string;
  site_id?: string;
  device_id?: string | null;
  camera_id?: string | null;
  zone_id?: string | null;
  incident_type?: string | null;
  severity?: string | null;
  from_ts?: string;
  to_ts?: string;
  total_incidents: number;
  reviewed_total: number;
  unresolved_total: number;
  items: IncidentReviewOutcomeRollupItem[];
  source_items: IncidentReviewOutcomeRollupSourceItem[];
  learning_signal_items?: IncidentReviewOutcomeLearningSignalItem[];
  risk_disposition_items?: IncidentReviewOutcomeRiskDispositionItem[];
  source_context_items?: IncidentSourceContextRollupItem[];
  semantic_metrics?: CustomerMetricSemantic[];
  review_population_key?: string;
  non_production_total?: number;
  customer_risk_total?: number;
  recurrence_signal_total?: number;
  production_claim_allowed_total?: number;
  real_world_outcome_claimed_total?: number;
  risk_reduction_claim_allowed_total?: number;
}

export interface IncidentReviewOutcomeHistoryBucket {
  bucket_start: string;
  bucket_type: 'day' | string;
  total_incidents: number;
  reviewed_total: number;
  unresolved_total: number;
  items: IncidentReviewOutcomeRollupItem[];
  source_items: IncidentReviewOutcomeRollupSourceItem[];
  learning_signal_items?: IncidentReviewOutcomeLearningSignalItem[];
  risk_disposition_items?: IncidentReviewOutcomeRiskDispositionItem[];
  source_context_items?: IncidentSourceContextRollupItem[];
  semantic_metrics?: CustomerMetricSemantic[];
  non_production_total?: number;
  customer_risk_total?: number;
  recurrence_signal_total?: number;
  production_claim_allowed_total?: number;
  real_world_outcome_claimed_total?: number;
  risk_reduction_claim_allowed_total?: number;
}

export interface IncidentReviewOutcomeHistoryResponse {
  ok: boolean;
  org_id?: string;
  site_id?: string;
  device_id?: string | null;
  zone_id?: string | null;
  from_ts?: string;
  to_ts?: string;
  bucket_type?: 'day' | string;
  history: IncidentReviewOutcomeHistoryBucket[];
}

export interface IncidentOwnershipRollupItem {
  ownership_state: 'unassigned' | 'assigned' | 'resolved' | string;
  count: number;
}

export interface IncidentOwnershipRollupOwnerItem {
  owner: string;
  count: number;
}

export interface IncidentOwnershipRollupResponse {
  ok: boolean;
  org_id?: string;
  site_id?: string;
  device_id?: string | null;
  zone_id?: string | null;
  from_ts?: string;
  to_ts?: string;
  total_incidents: number;
  assigned_total: number;
  resolved_total: number;
  unassigned_total: number;
  items: IncidentOwnershipRollupItem[];
  owner_items: IncidentOwnershipRollupOwnerItem[];
}

export interface IncidentCorrectiveActionRollupItem {
  corrective_action_state: string;
  count: number;
}

export interface IncidentCorrectiveActionWorkOrderStateItem {
  work_order_state: string;
  count: number;
}

export interface IncidentCorrectiveActionRollupResponse {
  ok: boolean;
  org_id?: string;
  site_id?: string;
  device_id?: string | null;
  zone_id?: string | null;
  from_ts?: string;
  to_ts?: string;
  total_incidents: number;
  work_order_total: number;
  resolved_total: number;
  not_started_total: number;
  items: IncidentCorrectiveActionRollupItem[];
  work_order_state_items: IncidentCorrectiveActionWorkOrderStateItem[];
}

export interface IncidentMeasurementRollupItem {
  measurement_state: string;
  count: number;
}

export interface IncidentMeasurementRollupResponse {
  ok: boolean;
  org_id?: string;
  site_id?: string;
  device_id?: string | null;
  zone_id?: string | null;
  from_ts?: string;
  to_ts?: string;
  total_incidents: number;
  applicable_total: number;
  ready_total: number;
  measured_total: number;
  items: IncidentMeasurementRollupItem[];
  source_context_items?: IncidentSourceContextRollupItem[];
  non_production_total?: number;
  production_claim_allowed_total?: number;
  real_world_outcome_claimed_total?: number;
  risk_reduction_claim_allowed_total?: number;
}

export interface IncidentActionSnapshotRollupItem {
  action_state: string;
  count: number;
}

export interface IncidentEscalationRollupItem {
  escalation_state: 'clear' | 'scheduled' | 'escalated' | 'overdue' | string;
  count: number;
}

export interface IncidentEscalationReasonRollupItem {
  reason: string;
  count: number;
}

export interface IncidentEscalationRollupResponse {
  ok: boolean;
  org_id?: string;
  site_id?: string;
  device_id?: string | null;
  zone_id?: string | null;
  from_ts?: string;
  to_ts?: string;
  total_incidents: number;
  escalated_total: number;
  overdue_total: number;
  scheduled_total: number;
  items: IncidentEscalationRollupItem[];
  reason_items: IncidentEscalationReasonRollupItem[];
}

export interface IncidentActionSnapshotRollupResponse {
  ok: boolean;
  org_id?: string;
  site_id?: string;
  device_id?: string | null;
  zone_id?: string | null;
  from_ts?: string;
  to_ts?: string;
  total_incidents: number;
  in_progress_total: number;
  scheduled_total: number;
  closed_total: number;
  items: IncidentActionSnapshotRollupItem[];
}

function _idempotencyKey(): string {
  try {
    // modern browsers
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const c: any = (globalThis as any).crypto;
    if (c?.randomUUID) return c.randomUUID();
  } catch {
    // ignore
  }
  return `ui_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function _throwIfApiError(data: any, fallback: string): void {
  const payload = data || {};
  if (payload && payload.ok === false) {
    const message = payload.detail || payload.error || fallback;
    throw new Error(String(message));
  }
  if (payload.ok !== true && typeof payload.error === 'string' && payload.error.trim()) {
    throw new Error(payload.error.trim());
  }
  if (typeof payload.error_msg === 'string' && payload.error_msg.trim()) {
    throw new Error(payload.error_msg.trim());
  }
}

function preserveNullableNumber(value: unknown): number | null | undefined {
  if (value === undefined) return undefined;
  if (value === null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function preserveNullableBoolean(value: unknown): boolean | null | undefined {
  if (value === undefined) return undefined;
  if (value === null) return null;
  return Boolean(value);
}

function preserveNullableString(value: unknown): string | null | undefined {
  if (value === undefined) return undefined;
  if (value === null) return null;
  const text = String(value).trim();
  return text || null;
}

// Helper to map API response to Incident interface
function mapApiIncident(item: any): Incident {
  const odooUrl = process.env.REACT_APP_ODOO_URL || 'http://localhost:8069';
  
  // Extract routing status from envelope_metadata.ticketing
  let routing_status: RoutingStatus | undefined;
  const envelope_metadata = item.envelope_metadata || {};
  let triggered_by: any = item.triggered_by || envelope_metadata.triggered_by;
  const policyNameJoin = String(item.policy_name || '').trim();
  if (policyNameJoin || item.policy_id) {
    const base =
      triggered_by && typeof triggered_by === 'object' && !Array.isArray(triggered_by)
        ? { ...triggered_by }
        : {};
    if (item.policy_id) base.policy_id = base.policy_id || item.policy_id;
    if (item.policy_type) base.policy_type = base.policy_type || item.policy_type;
    if (policyNameJoin && !String(base.policy_name || '').trim()) {
      base.policy_name = policyNameJoin;
    }
    if (Object.keys(base).length) triggered_by = base;
  }
  const ticketing = envelope_metadata.ticketing || {};
  if (Object.keys(ticketing).length > 0) {
    routing_status = {
      dashboard: ticketing.dashboard,
      odoo: ticketing.odoo,
      webhook: ticketing.webhook,
    };
  }
  
  const workflowStatusRaw = String(item.status || '').trim().toUpperCase();
  const workflowStatus: IncidentWorkflowStatus =
    (workflowStatusRaw === 'OPEN' ||
    workflowStatusRaw === 'ACKED' ||
    workflowStatusRaw === 'IN_REVIEW' ||
    workflowStatusRaw === 'CLOSED' ||
    workflowStatusRaw === 'SUPPRESSED')
      ? (workflowStatusRaw as IncidentWorkflowStatus)
      : (item.ts_end ? 'CLOSED' : 'OPEN');

  return {
    id: item.id,
    incident_id: item.incident_id,
    title: item.incident_type || item.incident_id || 'Incident',  // Use incident_type as title
    description: item.safety_category || item.description || '',
    severity: item.severity || 'medium',
    status: workflowStatus,
    ops_state: (() => {
      const s = String(item.ops_state || '').trim().toLowerCase();
      if (s === 'new' || s === 'acknowledged' || s === 'in_progress' || s === 'resolved' || s === 'closed') {
        return s as IncidentOpsState;
      }
      return undefined;
    })(),
    assignee: item.assignee ?? null,
    assignment_team: item.assignment_team ?? null,
    assigned_at: item.assigned_at || null,
    assigned_by: item.assigned_by || null,
    pattern_owner: item.pattern_owner ?? null,
    pattern_owner_team: item.pattern_owner_team ?? null,
    pattern_assigned_at: item.pattern_assigned_at || null,
    pattern_assigned_by: item.pattern_assigned_by || null,
    pattern_key: item.pattern_key ?? null,
    canonical_pattern_owner: item.canonical_pattern_owner ?? null,
    canonical_pattern_owner_team: item.canonical_pattern_owner_team ?? null,
    canonical_pattern_assigned_at: item.canonical_pattern_assigned_at || null,
    canonical_pattern_assigned_by: item.canonical_pattern_assigned_by || null,
    pattern_owner_display: item.pattern_owner_display ?? null,
    followup_owner: item.followup_owner ?? null,
    followup_team: item.followup_team ?? null,
    followup_next_review_at: item.followup_next_review_at || null,
    followup_updated_at: item.followup_updated_at || null,
    followup_updated_by: item.followup_updated_by || null,
    followup_owner_display: item.followup_owner_display ?? null,
    sla_target_at: item.sla_target_at || null,
    ops_overdue: Boolean(item.ops_overdue),
    ops_escalation_level: Number.isFinite(Number(item.ops_escalation_level))
      ? Number(item.ops_escalation_level)
      : (Number.isFinite(Number(item.escalation_level)) ? Number(item.escalation_level) : 0),
    ops_escalation_reasons: Array.isArray(item.ops_escalation_reasons)
      ? item.ops_escalation_reasons.map((x: any) => String(x || ''))
      : [],
    last_escalated_at: item.last_escalated_at || null,
    resolved_at: item.resolved_at || null,
    resolved_by: item.resolved_by || null,
    version: Number.isFinite(Number(item.version)) ? Number(item.version) : 1,
    assigned_to: item.assignee || null,
    created_at: item.created_at,
    updated_at: item.updated_at,
    zone_id: item.zone_id,
    device_id: item.device_id,
    camera_id: item.camera_id || envelope_metadata.camera_id,
    incident_type: item.incident_type,
    org_id: item.org_id,
    site_id: item.site_id,
    ts_start: item.ts_start,
    ts_end: item.ts_end,
    close_reason: item.close_reason || null,
    close_note: item.close_note || null,
    review_outcome: item.review_outcome || null,
    review_outcome_source: item.review_outcome_source || null,
    operator_tags: item.operator_tags,
    operator_note: item.operator_note || null,
    operator_note_updated_at: item.operator_note_updated_at || null,
    operator_note_updated_by: item.operator_note_updated_by || null,
    snapshot_status: item.snapshot_status || null,
    snapshot_uri: item.snapshot_uri || null,
    snapshot_error: item.snapshot_error || null,
    clip_status: item.clip_status || null,
    clip_uri: item.clip_uri || null,
    clip_error: item.clip_error || null,
    meta_status: item.meta_status || null,
    meta_uri: item.meta_uri || null,
    meta_error: item.meta_error || null,
    evidence: item.evidence || undefined,
    timing:
      item.timing && typeof item.timing === 'object'
        ? (item.timing as IncidentTiming)
        : undefined,
    review_media:
      item.review_media && typeof item.review_media === 'object'
        ? (item.review_media as IncidentReviewMedia)
        : undefined,
    actors: item.actors,
    metrics: item.metrics,
    envelope_metadata,
    triggered_by,
    policy_id: item.policy_id,
    policy_type: item.policy_type,
    policy_name:
      String((triggered_by && triggered_by.policy_name) || item.policy_name || '').trim() || undefined,
    canonical_labels:
      item.canonical_labels && typeof item.canonical_labels === 'object'
        ? (item.canonical_labels as CanonicalLabels)
        : undefined,
    proof_scope:
      item.proof_scope && typeof item.proof_scope === 'object'
        ? (item.proof_scope as ProofScopeSummary)
        : undefined,
    safety_case:
      item.safety_case && typeof item.safety_case === 'object'
        ? (item.safety_case as SafetyCase)
        : undefined,
    degraded: !!item.degraded,
    degraded_reason: item.degraded_reason || envelope_metadata?.degraded_reason || null,
    owner: item.owner || item?.taxonomy?.owner,
    taxonomy: item.taxonomy || undefined,
    raw: item,
    // Odoo fields
    odoo_ticket_id: item.odoo_ticket_id,
    odoo_ticket_state: item.odoo_ticket_state,
    odoo_synced_at: item.odoo_synced_at,
    // Prefer server-provided URL (it uses the server's ODOO_URL); fall back to local env for dev.
    odoo_ticket_url: item.odoo_ticket_url
      ? item.odoo_ticket_url
      : (item.odoo_ticket_id
          ? `${odooUrl}/web#id=${item.odoo_ticket_id}&model=safety.incident&view_type=form`
          : undefined),
    // Routing status
    routing_status,
    policy_notification_routes: derivePolicyNotificationRoutes(envelope_metadata),
    is_test: item.is_test || false,
    edge_state: item.edge_state ?? null,
    edge_active_at: item.edge_active_at ?? null,
    edge_cleared_at: item.edge_cleared_at ?? null,
    edge_clear_reason: item.edge_clear_reason ?? null,
    last_edge_update_at: item.last_edge_update_at ?? null,
    last_seen_at: item.last_seen_at || item.last_seen || null,
    last_seen: item.last_seen || item.last_seen_at || null,
    episode_count: preserveNullableNumber(item.episode_count),
    restricted_zone_occupancy_duration_ms: preserveNullableNumber(
      item.restricted_zone_occupancy_duration_ms,
    ),
    forklift_co_presence_entry_count: preserveNullableNumber(
      item.forklift_co_presence_entry_count,
    ),
    active_episode_count: preserveNullableNumber(item.active_episode_count),
    observed_track_count: preserveNullableNumber(item.observed_track_count),
    observation_count: preserveNullableNumber(item.observation_count),
    observed_pair_key_count: preserveNullableNumber(item.observed_pair_key_count),
    evidence_count: preserveNullableNumber(item.evidence_count),
    latest_episode_at: preserveNullableString(item.latest_episode_at),
    last_observed_at: preserveNullableString(item.last_observed_at),
    safety_episodes_available: preserveNullableBoolean(item.safety_episodes_available),
  };
}

// API Functions
export const incidentsApi = {
  /**
   * List incidents
   */
  listIncidents: async (
    status?: IncidentStatus,
    severity?: IncidentSeverity,
    limit: number = 100,
    offset: number = 0,
    fromDate?: string,
    toDate?: string,
    incidentType?: string,
    zoneId?: string,
    deviceId?: string,
    cameraId?: string,
    ops?: {
      assignee?: string;
      assignment_team?: string;
      ops_state?: IncidentOpsState | IncidentOpsState[];
      ops_queue?:
        | 'my_queue'
        | 'my_risk_queue'
        | 'assigned_by_me'
        | 'risk_assigned_by_me'
        | 'escalations'
        | 'overdue'
        | 'unassigned_high_risk';
      operator_id?: string;
      /** Skip expensive COUNT(*) for faster queue first paint. */
      skip_total?: boolean;
      /** Use queue/list summary mode to skip expensive evidence-status joins. */
      summary?: boolean;
      /** Optional. Rows that store a different condition_id are excluded. */
      condition_id?: string;
      /** Optional. Rows that store a different policy_id are excluded. */
      policy_id?: string;
      /** Inclusive event-time lower bound (UTC). Uses ts_start, then created_at. */
      occurred_from?: string;
      /** Exclusive event-time upper bound (UTC). */
      occurred_to?: string;
      /**
       * Individual Observations. Selects existing incident rows by pattern
       * member activity and qualifying episode time, not parent created_at.
       */
      activity_window?: 'member';
    },
    degraded?: boolean,
    utcHour?: number | null,
  ): Promise<{ incidents: Incident[]; count: number }> => {
    const params: Record<string, any> = { limit, offset };
    if (status) params.status = status;
    if (severity) params.severity = severity;
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    if (incidentType) params.incident_type = incidentType;
    if (zoneId) params.zone_id = zoneId;
    if (deviceId) params.device_id = deviceId;
    if (cameraId) params.camera_id = cameraId;
    if (typeof utcHour === 'number' && utcHour >= 0 && utcHour <= 23) params.utc_hour = utcHour;
    if (typeof degraded === 'boolean') params.degraded = degraded;
    if (ops?.assignee) params.assignee = String(ops.assignee || '').trim();
    if (ops?.operator_id) params.operator_id = String(ops.operator_id || '').trim();
    if (ops?.assignment_team) params.assignment_team = String(ops.assignment_team || '').trim();
    if (ops?.ops_queue) params.ops_queue = ops.ops_queue;
    if (ops?.skip_total) params.skip_total = true;
    if (ops?.summary) params.summary = true;
    if (ops?.condition_id) params.condition_id = String(ops.condition_id).trim();
    if (ops?.policy_id) params.policy_id = String(ops.policy_id).trim();
    if (ops?.occurred_from) params.occurred_from = String(ops.occurred_from).trim();
    if (ops?.occurred_to) params.occurred_to = String(ops.occurred_to).trim();
    if (ops?.activity_window === 'member') params.activity_window = 'member';
    if (ops?.ops_state) {
      if (Array.isArray(ops.ops_state)) params.ops_state = ops.ops_state.join(',');
      else params.ops_state = String(ops.ops_state || '').trim();
    }

    const timeout = ops?.summary ? 30000 : 300000;
    const response = await api.get('/api/v1/incidents', { params, timeout });
    _throwIfApiError(response.data, 'Failed to list incidents');
    
    // Map API response format to expected format
    const items = response.data.items || [];
    const total = response.data.total;
    return {
      incidents: items.map(mapApiIncident),
      count: typeof total === 'number' && Number.isFinite(total) ? total : items.length,
    };
  },

  /**
   * Individual Observations. One row per qualified safety episode.
   * Does not send activity_window. Grouped Patterns keep listIncidentGroups.
   */
  listSafetyEpisodeObservations: async (args: {
    status?: string;
    severity?: IncidentSeverity | string;
    limit?: number;
    offset?: number;
    fromDate?: string;
    toDate?: string;
    incidentType?: string;
    zoneId?: string;
    deviceId?: string;
    cameraId?: string;
    family?: string;
    policyId?: string;
    skip_total?: boolean;
  }): Promise<{ incidents: Incident[]; count: number }> => {
    const params: Record<string, any> = {
      limit: args.limit ?? 100,
      offset: args.offset ?? 0,
    };
    if (args.status) params.status = args.status;
    if (args.severity) params.severity = args.severity;
    if (args.fromDate) params.from_date = args.fromDate;
    if (args.toDate) params.to_date = args.toDate;
    if (args.incidentType) params.incident_type = args.incidentType;
    if (args.zoneId) params.zone_id = args.zoneId;
    if (args.deviceId) params.device_id = args.deviceId;
    if (args.cameraId) params.camera_id = args.cameraId;
    if (args.family) params.family = args.family;
    if (args.policyId) params.policy_id = args.policyId;
    if (args.skip_total) params.skip_total = true;
    // These stay on GET /incidents. Never restore them onto this endpoint
    // from a URL or localStorage, and never apply them silently.
    for (const forbidden of ['ops_queue', 'assignee', 'assignment_team', 'occurred_from', 'occurred_to', 'utc_hour', 'activity_window']) {
      delete params[forbidden];
    }
    const response = await api.get('/api/v1/incidents/safety-episodes', { params, timeout: 30000 });
    _throwIfApiError(response.data, 'Failed to list safety episodes');
    const items = Array.isArray(response.data?.items) ? response.data.items : [];
    const total = response.data?.total;
    return {
      incidents: items.map((item: any) => mapSafetyEpisodeObservation(item) as Incident),
      count: typeof total === 'number' && Number.isFinite(total) ? total : items.length,
    };
  },

  listIncidentEvidence: async (incidentId: string): Promise<Array<Record<string, any>>> => {
    const response = await api.get(
      `/api/v1/incidents/${encodeURIComponent(incidentId)}/evidence`,
    );
    const items = response.data?.items;
    return Array.isArray(items) ? items : [];
  },

  /**
   * Get incident by ID
   */
  getIncident: async (incidentId: string): Promise<Incident> => {
    const response = await api.get(`/api/v1/incidents/${incidentId}`);
    return mapApiIncident(response.data);
  },

  /**
   * Authoritative persisted Live Alerts snapshot (EPIC400 Phase 8B).
   * Read failure is returned as read_state=unavailable — never as a fake empty list.
   */
  getBuyerLiveAlerts: async (
    limitOrOpts: number | { limit?: number; includeClosed?: boolean } = 50,
  ): Promise<BuyerLiveResponse> => {
    const opts = typeof limitOrOpts === 'number' ? { limit: limitOrOpts } : (limitOrOpts || {});
    const limit = typeof opts.limit === 'number' ? opts.limit : 50;
    const params: Record<string, unknown> = { limit };
    if (opts.includeClosed) params.include_closed = true;
    const response = await api.get('/api/v1/incidents/buyer-live', { params });
    const data = response.data || {};
    const items = (Array.isArray(data.items) ? data.items : []).map((raw: any) => {
      const operational = raw?.operational && typeof raw.operational === 'object' ? raw.operational : {};
      return {
        operational: {
          ...operational,
          incident_id: String(operational.incident_id || ''),
        },
        claims: raw?.claims,
        claim_denials: raw?.claim_denials,
      } as BuyerLiveItem;
    });
    return {
      ok: data.ok !== false && data.read_state !== 'unavailable',
      read_state: String(data.read_state || (data.ok === false ? 'unavailable' : 'ok')),
      items,
      error: data.error,
      list_incomplete: data.list_incomplete === true || data.read_state === 'partial',
      snapshot_complete: data.snapshot_complete === true,
      incomplete_list_message: data.incomplete_list_message,
      unavailable_families: Array.isArray(data.unavailable_families) ? data.unavailable_families : [],
    };
  },

  /**
   * Persisted Alert History (open + closed). Same buyer projection family as Live Alerts.
   * Not Grafana metrics history.
   */
  getBuyerAlertHistory: async (limit = 100): Promise<BuyerLiveResponse> => {
    return incidentsApi.getBuyerLiveAlerts({ limit, includeClosed: true });
  },

  getIncidentGroupHeatmapOccurrence: async (params: {
    dimension: 'zone' | 'camera';
    id: string;
    hour: number;
    from_date?: string;
    to_date?: string;
    incident_type?: string;
    zone_id?: string;
    device_id?: string;
    camera_id?: string;
  }): Promise<Incident> => {
    const response = await api.get('/api/v1/incidents/groups/heatmap/open-occurrence', {
      params: {
        dimension: params.dimension,
        id: params.id,
        utc_hour: params.hour,
        from_date: params.from_date,
        to_date: params.to_date,
        incident_type: params.incident_type,
        zone_id: params.zone_id,
        device_id: params.device_id,
        camera_id: params.camera_id,
      },
      timeout: 25000,
    });
    _throwIfApiError(response.data, 'Failed to open occurrence for that hour');
    return mapApiIncident(response.data);
  },

  /**
   * Create new incident
   */
  createIncident: async (data: CreateIncidentRequest): Promise<{ id: string; success: boolean }> => {
    // Map frontend fields to API fields
    const apiData = {
      incident_type: data.incident_type || data.title || 'manual',
      severity: data.severity,
      zone_id: data.zone_id,
      device_id: data.device_id,
      safety_category: data.description,
    };
    
    const response = await api.post('/api/v1/incidents', apiData);
    return {
      id: response.data.id || response.data.incident_id,
      success: true,
    };
  },

  /**
   * Update incident
   */
  updateIncident: async (
    incidentId: string,
    updates: Partial<Pick<Incident, 'severity' | 'ts_end'>>
  ): Promise<{ id: string; success: boolean }> => {
    const response = await api.put(`/api/v1/incidents/${incidentId}`, updates);
    return {
      id: response.data.incident_id,
      success: true,
    };
  },

  /**
   * Gate 3: workflow status transition (idempotent + versioned)
   */
  patchStatus: async (
    incidentId: string,
    input: {
      status: IncidentWorkflowStatus;
      close_reason?: IncidentCloseReason;
      close_note?: string;
      review_outcome?: IncidentReviewOutcome;
      case_decision?: 'accepted' | 'rejected' | 'deferred' | 'already_managed' | 'excluded';
      group_key?: string;
      from_date?: string;
      to_date?: string;
      operator_tags?: any;
    },
    opts: { expected_version: number; idempotency_key?: string }
  ): Promise<Incident> => {
    const headers: Record<string, string> = {
      'Idempotency-Key': opts.idempotency_key || _idempotencyKey(),
      'X-Expected-Version': String(opts.expected_version),
    };
    const retryDelaysMs = [1200, 2000, 3200];
    for (let attempt = 0; attempt <= retryDelaysMs.length; attempt += 1) {
      try {
        const res = await api.patch(
          `/api/v1/incidents/${encodeURIComponent(incidentId)}/status`,
          input,
          { headers, timeout: INCIDENT_WORKFLOW_WRITE_TIMEOUT_MS },
        );
        const payload = res.data?.incident || res.data;
        if (payload?.detail_included === false || !payload?.safety_case) {
          return await incidentsApi.getIncident(incidentId);
        }
        return mapApiIncident(payload);
      } catch (error: any) {
        const apiError = String(error?.response?.data?.error || '').trim();
        const retryableBusy = apiError === 'incident_busy' && attempt < retryDelaysMs.length;
        if (!retryableBusy) throw error;
        await new Promise((resolve) => globalThis.setTimeout(resolve, retryDelaysMs[attempt]));
      }
    }
    throw new Error('Failed to patch incident status');
  },

  /**
   * Item 4: operations update (assignment + SLA + ops lifecycle).
   */
  patchOps: async (
    incidentId: string,
    input: {
      ops_state?: IncidentOpsState;
      assignee?: string | null;
      assignment_team?: string | null;
      sla_target_at?: string | null;
      followup_owner?: string | null;
      followup_team?: string | null;
      followup_next_review_at?: string | null;
    },
    opts: { expected_version: number; idempotency_key?: string }
  ): Promise<Incident> => {
    const headers: Record<string, string> = {
      'Idempotency-Key': opts.idempotency_key || _idempotencyKey(),
      'X-Expected-Version': String(opts.expected_version),
    };
    const retryDelaysMs = [1200, 2000, 3200];
    for (let attempt = 0; attempt <= retryDelaysMs.length; attempt += 1) {
      try {
        const res = await api.patch(`/api/v1/incidents/${encodeURIComponent(incidentId)}/ops`, input, {
          headers,
          timeout: INCIDENT_WORKFLOW_WRITE_TIMEOUT_MS,
        });
        const payload = res.data?.incident || res.data;
        if (payload?.detail_included === false || !payload?.safety_case) {
          return await incidentsApi.getIncident(incidentId);
        }
        return mapApiIncident(payload);
      } catch (error: any) {
        const apiError = String(error?.response?.data?.error || '').trim();
        const retryableBusy = apiError === 'incident_busy' && attempt < retryDelaysMs.length;
        if (!retryableBusy) throw error;
        await new Promise((resolve) => globalThis.setTimeout(resolve, retryDelaysMs[attempt]));
      }
    }
    throw new Error('Failed to patch incident ops');
  },

  patchMeasurement: async (
    incidentId: string,
    input: IncidentMeasurementPatchInput,
    opts: { expected_version: number; idempotency_key?: string }
  ): Promise<Incident> => {
    const headers: Record<string, string> = {
      'Idempotency-Key': opts.idempotency_key || _idempotencyKey(),
      'X-Expected-Version': String(opts.expected_version),
    };
    const retryDelaysMs = [1200, 2000, 3200];
    for (let attempt = 0; attempt <= retryDelaysMs.length; attempt += 1) {
      try {
        const res = await api.patch(
          `/api/v1/incidents/${encodeURIComponent(incidentId)}/measurement`,
          input,
          { headers, timeout: INCIDENT_WORKFLOW_WRITE_TIMEOUT_MS },
        );
        const payload = res.data?.incident || res.data;
        if (payload?.detail_included === false || !payload?.safety_case) {
          return await incidentsApi.getIncident(incidentId);
        }
        return mapApiIncident(payload);
      } catch (error: any) {
        const apiError = String(error?.response?.data?.error || '').trim();
        const retryableBusy = apiError === 'incident_busy' && attempt < retryDelaysMs.length;
        if (!retryableBusy) throw error;
        await new Promise((resolve) => globalThis.setTimeout(resolve, retryDelaysMs[attempt]));
      }
    }
    throw new Error('Failed to patch incident measurement');
  },

  patchCorrectiveAction: async (
    incidentId: string,
    input: IncidentCorrectiveActionPatchInput,
    opts: { expected_version: number; idempotency_key?: string },
  ): Promise<Incident> => {
    const headers: Record<string, string> = {
      'Idempotency-Key': opts.idempotency_key || _idempotencyKey(),
      'X-Expected-Version': String(opts.expected_version),
    };
    const retryDelaysMs = [1200, 2000, 3200];
    for (let attempt = 0; attempt <= retryDelaysMs.length; attempt += 1) {
      try {
        const res = await api.patch(
          `/api/v1/incidents/${encodeURIComponent(incidentId)}/corrective-action`,
          input,
          { headers, timeout: INCIDENT_WORKFLOW_WRITE_TIMEOUT_MS },
        );
        const payload = res.data?.incident || res.data;
        if (payload?.detail_included === false || !payload?.safety_case) {
          return await incidentsApi.getIncident(incidentId);
        }
        return mapApiIncident(payload);
      } catch (error: any) {
        const apiError = String(error?.response?.data?.error || '').trim();
        const retryableBusy = apiError === 'incident_busy' && attempt < retryDelaysMs.length;
        if (!retryableBusy) throw error;
        await new Promise((resolve) => globalThis.setTimeout(resolve, retryDelaysMs[attempt]));
      }
    }
    throw new Error('Failed to patch incident corrective action');
  },

  /**
   * Gate 3: operator note (idempotent + versioned)
   */
  putNote: async (
    incidentId: string,
    note: string,
    opts: { expected_version: number; idempotency_key?: string }
  ): Promise<Incident> => {
    const headers: Record<string, string> = {
      'Idempotency-Key': opts.idempotency_key || _idempotencyKey(),
      'X-Expected-Version': String(opts.expected_version),
    };
    const res = await api.put(`/api/v1/incidents/${encodeURIComponent(incidentId)}/note`, { note }, { headers });
    return mapApiIncident(res.data?.incident || res.data);
  },

  listComments: async (
    incidentId: string,
    params?: { limit?: number; offset?: number }
  ): Promise<{ items: IncidentComment[]; limit: number; offset: number }> => {
    const res = await api.get(`/api/v1/incidents/${encodeURIComponent(incidentId)}/comments`, { params: params || {} });
    _throwIfApiError(res.data, 'Failed to fetch incident comments');
    return {
      items: Array.isArray(res.data?.items) ? res.data.items as IncidentComment[] : [],
      limit: Number(res.data?.limit || params?.limit || 100),
      offset: Number(res.data?.offset || params?.offset || 0),
    };
  },

  addComment: async (
    incidentId: string,
    text: string
  ): Promise<{ incident?: Incident; comment?: IncidentComment | null }> => {
    const res = await api.post(`/api/v1/incidents/${encodeURIComponent(incidentId)}/comments`, { text });
    _throwIfApiError(res.data, 'Failed to add incident comment');
    return {
      incident: res.data?.incident ? mapApiIncident(res.data.incident) : undefined,
      comment: (res.data?.comment || null) as IncidentComment | null,
    };
  },

  adjustThreshold: async (
    incidentId: string,
    input: {
      adjustment: {
        confidence_threshold?: number;
        persistence_seconds?: number;
        cooldown_seconds?: number;
        threshold_seconds?: number;
      };
    }
  ): Promise<IncidentThresholdAdjustmentResponse> => {
    const res = await api.post(`/api/v1/incidents/${encodeURIComponent(incidentId)}/threshold-adjustment`, input);
    _throwIfApiError(res.data, 'Failed to adjust threshold');
    return {
      incident: res.data?.incident ? mapApiIncident(res.data.incident) : undefined,
      policy: res.data?.policy,
    };
  },

  /**
   * Gate 3: severity override (idempotent + versioned)
   */
  putSeverityOverride: async (
    incidentId: string,
    severity: IncidentSeverity,
    opts: { expected_version: number; idempotency_key?: string }
  ): Promise<Incident> => {
    const headers: Record<string, string> = {
      'Idempotency-Key': opts.idempotency_key || _idempotencyKey(),
      'X-Expected-Version': String(opts.expected_version),
    };
    const res = await api.put(
      `/api/v1/incidents/${encodeURIComponent(incidentId)}/severity-override`,
      { severity },
      { headers }
    );
    return mapApiIncident(res.data?.incident || res.data);
  },

  /**
   * Gate 3: suppress similar (cloud SoR, edge apply side-effect)
   */
  suppressSimilar: async (
    incidentId: string,
    input: {
      duration_seconds: number;
      reason?: string;
      case_decision?: 'accepted' | 'rejected' | 'deferred' | 'already_managed' | 'excluded';
      group_key?: string;
      from_date?: string;
      to_date?: string;
      scope?: { camera_id?: string; zone_id?: string; incident_type?: string };
    },
  ): Promise<{ ok: boolean; suppression?: any; edge_apply?: any }> => {
    const res = await api.post(`/api/v1/incidents/${encodeURIComponent(incidentId)}/suppress-similar`, input);
    return res.data;
  },

  /** Request one-time edge clip upload when clip is local-only. */
  promoteClip: async (
    incidentId: string,
  ): Promise<{ ok: boolean; clip_promotion_state?: string; promote?: Record<string, unknown> }> => {
    const res = await api.post(`/api/v1/incidents/${encodeURIComponent(incidentId)}/promote-clip`);
    return res.data;
  },

  /** Request snapshot and/or clip upload from edge (operator-driven). */
  promoteEvidence: async (
    incidentId: string,
    opts?: {
      snapshot?: boolean;
      clip?: boolean;
      /** Maps to edge HOT queue priority (e.g. ultra_hot = 250). */
      promote_priority?: number | 'ultra_hot' | 'viewer' | 'operator';
    },
  ): Promise<{
    ok: boolean;
    promote_snapshot?: Record<string, unknown>;
    promote_clip?: Record<string, unknown>;
    clip_promotion_state?: string;
  }> => {
    const body: Record<string, unknown> = {
      snapshot: opts?.snapshot !== false,
      clip: opts?.clip !== false,
    };
    if (opts?.promote_priority !== undefined && opts?.promote_priority !== null) {
      body.promote_priority = opts.promote_priority;
    }
    const res = await api.post(`/api/v1/incidents/${encodeURIComponent(incidentId)}/promote-evidence`, body);
    return res.data;
  },

  /**
   * Gate 3: audit events for incident
   */
  listAudit: async (
    incidentId: string,
    params?: { limit?: number; offset?: number }
  ): Promise<{ items: any[]; limit: number; offset: number }> => {
    const res = await api.get(`/api/v1/incidents/${encodeURIComponent(incidentId)}/audit`, { params: params || {} });
    _throwIfApiError(res.data, 'Failed to fetch incident audit');
    return res.data;
  },

  listActivity: async (
    incidentId: string,
    params?: { limit?: number }
  ): Promise<{ items: IncidentActivityItem[]; limit: number }> => {
    const res = await api.get(`/api/v1/incidents/${encodeURIComponent(incidentId)}/activity`, { params: params || {} });
    _throwIfApiError(res.data, 'Failed to fetch incident activity');
    return {
      items: Array.isArray(res.data?.items) ? res.data.items as IncidentActivityItem[] : [],
      limit: Number(res.data?.limit || params?.limit || 200),
    };
  },

  listIncidentSafetyEpisodes: async (incidentId: string): Promise<IncidentSafetyEpisodesResponse> => {
    const res = await api.get(`/api/v1/incidents/${encodeURIComponent(incidentId)}/safety-episodes`);
    _throwIfApiError(res.data, 'Failed to fetch incident safety episodes');
    return {
      incident_id: String(res.data?.incident_id || incidentId),
      available: Boolean(res.data?.available),
      episodes: Array.isArray(res.data?.episodes) ? res.data.episodes as IncidentSafetyEpisode[] : [],
      error: res.data?.error ?? null,
    };
  },

  getEdgeOutboxSummary: async (deviceId: string): Promise<EdgeOutboxSummaryResponse> => {
    const res = await api.get(`/api/v1/pipeline/devices/${encodeURIComponent(deviceId)}/edge/outbox/summary`);
    return (res?.data || {}) as EdgeOutboxSummaryResponse;
  },

  getEdgeOutboxIncident: async (
    deviceId: string,
    incidentId: string,
    params?: { limit?: number }
  ): Promise<EdgeOutboxIncidentResponse> => {
    const res = await api.get(
      `/api/v1/pipeline/devices/${encodeURIComponent(deviceId)}/edge/outbox/incident/${encodeURIComponent(incidentId)}`,
      { params: params || {} },
    );
    return (res?.data || {}) as EdgeOutboxIncidentResponse;
  },

  /**
   * Get incident statistics
   */
  getStats: async (): Promise<IncidentStats> => {
    const response = await api.get('/api/v1/incidents/stats');
    _throwIfApiError(response.data, 'Failed to fetch incident stats');
    return {
      total: response.data.total || 0,
      open: response.data.open || 0,
      closed: response.data.closed || 0,
      critical: response.data.critical || 0,
      high: response.data.high || 0,
      resolution_rate: response.data.resolution_rate || 0,
    };
  },

  /**
   * KPI counters for the Incident Management top tiles.
   */
  getKpis: async (params?: { from_date?: string; to_date?: string }): Promise<IncidentKpis> => {
    const response = await api.get('/api/v1/incidents/kpis', { params: params || {}, timeout: 300000 });
    _throwIfApiError(response.data, 'Failed to fetch incident KPIs');
    return {
      open_now: response.data.open_now || 0,
      new_today: response.data.new_today || 0,
      acknowledged: response.data.acknowledged || 0,
      resolved_today: response.data.resolved_today || 0,
      sla_breaches: response.data.sla_breaches || 0,
      as_of: response.data.as_of,
      day_start_utc: response.data.day_start_utc,
      range_from: response.data.range_from,
      range_to: response.data.range_to,
    };
  },

  /**
   * Item 4 queue views: my queue, overdue, unassigned high-risk, escalations.
   */
  getOpsQueues: async (params?: { assignee?: string; limit?: number }): Promise<IncidentOpsQueuesResponse> => {
    const response = await api.get('/api/v1/incidents/ops/queues', { params: params || {}, timeout: 300000 });
    _throwIfApiError(response.data, 'Failed to fetch ops queues');
    const data = (response.data || {}) as IncidentOpsQueuesResponse;
    const mapItems = (items: any[]) => (Array.isArray(items) ? items.map(mapApiIncident) : []);
    return {
      ...data,
      queues: {
        my_queue: {
          count: Number((data as any)?.queues?.my_queue?.count || 0),
          items: mapItems((data as any)?.queues?.my_queue?.items || []),
        },
        my_risk_queue: {
          count: Number((data as any)?.queues?.my_risk_queue?.count || 0),
          items: mapItems((data as any)?.queues?.my_risk_queue?.items || []),
        },
        assigned_by_me: {
          count: Number((data as any)?.queues?.assigned_by_me?.count || 0),
          items: mapItems((data as any)?.queues?.assigned_by_me?.items || []),
        },
        risk_assigned_by_me: {
          count: Number((data as any)?.queues?.risk_assigned_by_me?.count || 0),
          items: mapItems((data as any)?.queues?.risk_assigned_by_me?.items || []),
        },
        overdue: {
          count: Number((data as any)?.queues?.overdue?.count || 0),
          items: mapItems((data as any)?.queues?.overdue?.items || []),
        },
        unassigned_high_risk: {
          count: Number((data as any)?.queues?.unassigned_high_risk?.count || 0),
          items: mapItems((data as any)?.queues?.unassigned_high_risk?.items || []),
        },
        escalations: {
          count: Number((data as any)?.queues?.escalations?.count || 0),
          items: mapItems((data as any)?.queues?.escalations?.items || []),
        },
      },
    };
  },

  /**
   * Group repeated incident rows into operator-friendly incident groups.
   */
  listIncidentGroups: async (
    params?: {
      /** open | closed | all — 'all' = no status predicate (time range only). */
      status?: 'open' | 'closed' | 'all';
      hours?: number;
      from_date?: string;
      to_date?: string;
      incident_type?: string;
      severity?: IncidentSeverity;
      zone_id?: string;
      device_id?: string;
      camera_id?: string;
      /** Max pattern rows (default 200 server-side). */
      limit?: number;
      /** Prefer persisted incident-episode grouping over legacy rollups. */
      prefer_persisted?: boolean;
    },
    options?: {
      /** Override axios timeout (ms). EPIC290: persisted 20s / fallback 8s. */
      timeoutMs?: number;
      signal?: AbortSignal;
    },
  ): Promise<{ items: IncidentGroup[]; as_of?: string; truncated?: boolean }> => {
    const timeout = typeof options?.timeoutMs === 'number' ? options.timeoutMs : 20_000;
    const response = await api.get('/api/v1/incidents/groups', {
      params: params || {},
      timeout,
      signal: options?.signal,
    });
    _throwIfApiError(response.data, 'Failed to fetch incident groups');
    return {
      items: (response.data.items || []).map((item: any) => ({
        ...item,
        incident_id: item?.incident_id || item?.linked_detailed_record_id || null,
        started_at: item?.started_at || item?.first_seen || null,
        last_seen_at: item?.last_seen_at || item?.last_seen || null,
        evidence_count:
          typeof item?.evidence_count === 'number'
            ? item.evidence_count
            : Number(item?.alert_count || 0),
        is_grouped:
          typeof item?.is_grouped === 'boolean'
            ? item.is_grouped
            : Number(item?.alert_count || 0) > 1,
      })) as IncidentGroup[],
      as_of: response.data.as_of,
      truncated: Boolean(response.data.truncated),
    };
  },

  /**
   * EPIC 89: pattern-level action on all incidents in a group window.
   */
  applyIncidentGroupAction: async (
    groupKey: string,
    body: IncidentGroupActionRequest,
  ): Promise<IncidentGroupActionResponse> => {
    const encodedKey = encodeURIComponent(groupKey);
    const response = await api.post(
      `/api/v1/incidents/groups/${encodedKey}/actions`,
      body,
      { timeout: 300000 },
    );
    _throwIfApiError(response.data, 'Failed to apply incident group action');
    const result = response.data as IncidentGroupActionResponse;
    if (!body.dry_run) {
      if (result.dry_run) {
        throw new Error('Server returned a preview-only response for an execute request.');
      }
      if (result.affected_count == null || Number.isNaN(Number(result.affected_count))) {
        throw new Error('Server response missing affected_count.');
      }
    }
    return result;
  },

  getIncidentGroupHeatmap: async (params?: {
    dimension?: 'zone' | 'camera';
    hours?: number;
    from_date?: string;
    to_date?: string;
    incident_type?: string;
    zone_id?: string;
    camera_id?: string;
    device_id?: string;
    limit_rows?: number;
  }): Promise<IncidentGroupHeatmapResponse> => {
    const response = await api.get('/api/v1/incidents/groups/heatmap', {
      params: params || {},
      timeout: 30000,
    });
    _throwIfApiError(response.data, 'Failed to fetch incident group heatmap');
    return {
      ok: Boolean(response.data?.ok),
      dimension: response.data?.dimension === 'camera' ? 'camera' : 'zone',
      item_semantics: response.data?.item_semantics || 'grouped_incident_patterns',
      definition: response.data?.definition,
      hours: Array.isArray(response.data?.hours) ? response.data.hours.map((value: any) => Number(value || 0)) : [],
      rows: Array.isArray(response.data?.rows)
        ? response.data.rows.map((row: any) => ({
            id: String(row?.id || '').trim() || '__unassigned__',
            total_count: Number(row?.total_count || 0),
            cells: Array.isArray(row?.cells)
              ? row.cells.map((cell: any) => ({
                  hour: Number(cell?.hour || 0),
                  count: Number(cell?.count || 0),
                  active_days: Number(cell?.active_days || 0),
                }))
              : [],
          }))
        : [],
      filters: response.data?.filters || {},
      from_ts: response.data?.from_ts,
      to_ts: response.data?.to_ts,
    };
  },

  getReviewOutcomeRollup: async (params: {
    org_id?: string;
    site_id?: string;
    from_ts: string;
    to_ts: string;
    device_id?: string;
    camera_id?: string;
    zone_id?: string;
    incident_type?: string;
    severity?: string;
  }): Promise<IncidentReviewOutcomeRollupResponse> => {
    const response = await api.get('/api/v1/incidents/review_outcomes/rollup', {
      params,
      timeout: 300000,
    });
    _throwIfApiError(response.data, 'Failed to fetch review outcome rollup');
    return {
      ok: Boolean(response.data?.ok),
      org_id: response.data?.org_id,
      site_id: response.data?.site_id,
      device_id: response.data?.device_id ?? null,
      camera_id: response.data?.camera_id ?? null,
      zone_id: response.data?.zone_id ?? null,
      incident_type: response.data?.incident_type ?? null,
      severity: response.data?.severity ?? null,
      from_ts: response.data?.from_ts,
      to_ts: response.data?.to_ts,
      total_incidents: Number(response.data?.total_incidents || 0),
      reviewed_total: Number(response.data?.reviewed_total || 0),
      unresolved_total: Number(response.data?.unresolved_total || 0),
      items: Array.isArray(response.data?.items)
        ? response.data.items.map((item: any) => ({
            review_outcome: String(item?.review_outcome || '').trim(),
            count: Number(item?.count || 0),
          }))
        : [],
      source_items: Array.isArray(response.data?.source_items)
        ? response.data.source_items.map((item: any) => ({
            review_outcome_source: String(item?.review_outcome_source || '').trim(),
            count: Number(item?.count || 0),
          }))
        : [],
      learning_signal_items: Array.isArray(response.data?.learning_signal_items)
        ? response.data.learning_signal_items.map((item: any) => ({
            learning_signal: String(item?.learning_signal || '').trim(),
            count: Number(item?.count || 0),
          }))
        : [],
      risk_disposition_items: Array.isArray(response.data?.risk_disposition_items)
        ? response.data.risk_disposition_items.map((item: any) => ({
            risk_disposition: String(item?.risk_disposition || '').trim(),
            count: Number(item?.count || 0),
          }))
        : [],
      semantic_metrics: Array.isArray(response.data?.semantic_metrics)
        ? response.data.semantic_metrics.map((metric: any) => parseCustomerMetricSemantic(metric))
        : [],
      review_population_key: String(response.data?.review_population_key || '').trim() || undefined,
      customer_risk_total: Number(response.data?.customer_risk_total || 0),
      recurrence_signal_total: Number(response.data?.recurrence_signal_total || 0),
      production_claim_allowed_total: Number(response.data?.production_claim_allowed_total || 0),
    };
  },

  getReviewOutcomeHistory: async (params: {
    org_id?: string;
    site_id?: string;
    from_ts: string;
    to_ts: string;
    device_id?: string;
    zone_id?: string;
  }): Promise<IncidentReviewOutcomeHistoryResponse> => {
    const response = await api.get('/api/v1/incidents/review_outcomes/history', {
      params,
      timeout: 300000,
    });
    _throwIfApiError(response.data, 'Failed to fetch review outcome history');
    return {
      ok: Boolean(response.data?.ok),
      org_id: response.data?.org_id,
      site_id: response.data?.site_id,
      device_id: response.data?.device_id ?? null,
      zone_id: response.data?.zone_id ?? null,
      from_ts: response.data?.from_ts,
      to_ts: response.data?.to_ts,
      bucket_type: response.data?.bucket_type,
      history: Array.isArray(response.data?.history)
        ? response.data.history.map((bucket: any) => ({
            bucket_start: String(bucket?.bucket_start || '').trim(),
            bucket_type: String(bucket?.bucket_type || 'day').trim() || 'day',
            total_incidents: Number(bucket?.total_incidents || 0),
            reviewed_total: Number(bucket?.reviewed_total || 0),
            unresolved_total: Number(bucket?.unresolved_total || 0),
            items: Array.isArray(bucket?.items)
              ? bucket.items.map((item: any) => ({
                  review_outcome: String(item?.review_outcome || '').trim(),
                  count: Number(item?.count || 0),
                }))
              : [],
            source_items: Array.isArray(bucket?.source_items)
              ? bucket.source_items.map((item: any) => ({
                  review_outcome_source: String(item?.review_outcome_source || '').trim(),
                  count: Number(item?.count || 0),
                }))
              : [],
            learning_signal_items: Array.isArray(bucket?.learning_signal_items)
              ? bucket.learning_signal_items.map((item: any) => ({
                  learning_signal: String(item?.learning_signal || '').trim(),
                  count: Number(item?.count || 0),
                }))
              : [],
            risk_disposition_items: Array.isArray(bucket?.risk_disposition_items)
              ? bucket.risk_disposition_items.map((item: any) => ({
                  risk_disposition: String(item?.risk_disposition || '').trim(),
                  count: Number(item?.count || 0),
                }))
              : [],
            customer_risk_total: Number(bucket?.customer_risk_total || 0),
            recurrence_signal_total: Number(bucket?.recurrence_signal_total || 0),
            production_claim_allowed_total: Number(bucket?.production_claim_allowed_total || 0),
          }))
        : [],
    };
  },

  getOwnershipRollup: async (params: {
    org_id?: string;
    site_id?: string;
    from_ts: string;
    to_ts: string;
    device_id?: string;
    zone_id?: string;
  }): Promise<IncidentOwnershipRollupResponse> => {
    const response = await api.get('/api/v1/incidents/ownership/rollup', {
      params,
      timeout: 300000,
    });
    _throwIfApiError(response.data, 'Failed to fetch ownership rollup');
    return {
      ok: Boolean(response.data?.ok),
      org_id: response.data?.org_id,
      site_id: response.data?.site_id,
      device_id: response.data?.device_id ?? null,
      zone_id: response.data?.zone_id ?? null,
      from_ts: response.data?.from_ts,
      to_ts: response.data?.to_ts,
      total_incidents: Number(response.data?.total_incidents || 0),
      assigned_total: Number(response.data?.assigned_total || 0),
      resolved_total: Number(response.data?.resolved_total || 0),
      unassigned_total: Number(response.data?.unassigned_total || 0),
      items: Array.isArray(response.data?.items)
        ? response.data.items.map((item: any) => ({
            ownership_state: String(item?.ownership_state || '').trim(),
            count: Number(item?.count || 0),
          }))
        : [],
      owner_items: Array.isArray(response.data?.owner_items)
        ? response.data.owner_items.map((item: any) => ({
            owner: String(item?.owner || '').trim(),
            count: Number(item?.count || 0),
          }))
        : [],
    };
  },

  getCorrectiveActionRollup: async (params: {
    org_id?: string;
    site_id?: string;
    from_ts: string;
    to_ts: string;
    device_id?: string;
    zone_id?: string;
  }): Promise<IncidentCorrectiveActionRollupResponse> => {
    const response = await api.get('/api/v1/incidents/corrective_actions/rollup', {
      params,
      timeout: 300000,
    });
    _throwIfApiError(response.data, 'Failed to fetch corrective action rollup');
    return {
      ok: Boolean(response.data?.ok),
      org_id: response.data?.org_id,
      site_id: response.data?.site_id,
      device_id: response.data?.device_id ?? null,
      zone_id: response.data?.zone_id ?? null,
      from_ts: response.data?.from_ts,
      to_ts: response.data?.to_ts,
      total_incidents: Number(response.data?.total_incidents || 0),
      work_order_total: Number(response.data?.work_order_total || 0),
      resolved_total: Number(response.data?.resolved_total || 0),
      not_started_total: Number(response.data?.not_started_total || 0),
      items: Array.isArray(response.data?.items)
        ? response.data.items.map((item: any) => ({
            corrective_action_state: String(item?.corrective_action_state || '').trim(),
            count: Number(item?.count || 0),
          }))
        : [],
      work_order_state_items: Array.isArray(response.data?.work_order_state_items)
        ? response.data.work_order_state_items.map((item: any) => ({
            work_order_state: String(item?.work_order_state || '').trim(),
            count: Number(item?.count || 0),
          }))
        : [],
    };
  },

  getMeasurementRollup: async (params: {
    org_id?: string;
    site_id?: string;
    from_ts: string;
    to_ts: string;
    device_id?: string;
    zone_id?: string;
  }): Promise<IncidentMeasurementRollupResponse> => {
    const response = await api.get('/api/v1/incidents/measurement/rollup', {
      params,
      timeout: 300000,
    });
    _throwIfApiError(response.data, 'Failed to fetch measurement rollup');
    return {
      ok: Boolean(response.data?.ok),
      org_id: response.data?.org_id,
      site_id: response.data?.site_id,
      device_id: response.data?.device_id ?? null,
      zone_id: response.data?.zone_id ?? null,
      from_ts: response.data?.from_ts,
      to_ts: response.data?.to_ts,
      total_incidents: Number(response.data?.total_incidents || 0),
      applicable_total: Number(response.data?.applicable_total || 0),
      ready_total: Number(response.data?.ready_total || 0),
      measured_total: Number(response.data?.measured_total || 0),
      items: Array.isArray(response.data?.items)
        ? response.data.items.map((item: any) => ({
            measurement_state: String(item?.measurement_state || '').trim(),
            count: Number(item?.count || 0),
          }))
        : [],
    };
  },

  getActionSnapshotRollup: async (params: {
    org_id?: string;
    site_id?: string;
    from_ts: string;
    to_ts: string;
    device_id?: string;
    zone_id?: string;
  }): Promise<IncidentActionSnapshotRollupResponse> => {
    const response = await api.get('/api/v1/incidents/action_snapshot/rollup', {
      params,
      timeout: 300000,
    });
    _throwIfApiError(response.data, 'Failed to fetch action snapshot rollup');
    return {
      ok: Boolean(response.data?.ok),
      org_id: response.data?.org_id,
      site_id: response.data?.site_id,
      device_id: response.data?.device_id ?? null,
      zone_id: response.data?.zone_id ?? null,
      from_ts: response.data?.from_ts,
      to_ts: response.data?.to_ts,
      total_incidents: Number(response.data?.total_incidents || 0),
      in_progress_total: Number(response.data?.in_progress_total || 0),
      scheduled_total: Number(response.data?.scheduled_total || 0),
      closed_total: Number(response.data?.closed_total || 0),
      items: Array.isArray(response.data?.items)
        ? response.data.items.map((item: any) => ({
            action_state: String(item?.action_state || '').trim(),
            count: Number(item?.count || 0),
          }))
        : [],
    };
  },

  getEscalationRollup: async (params: {
    org_id?: string;
    site_id?: string;
    from_ts: string;
    to_ts: string;
    device_id?: string;
    zone_id?: string;
  }): Promise<IncidentEscalationRollupResponse> => {
    const response = await api.get('/api/v1/incidents/escalation/rollup', {
      params,
      timeout: 300000,
    });
    _throwIfApiError(response.data, 'Failed to fetch escalation rollup');
    return {
      ok: Boolean(response.data?.ok),
      org_id: response.data?.org_id,
      site_id: response.data?.site_id,
      device_id: response.data?.device_id ?? null,
      zone_id: response.data?.zone_id ?? null,
      from_ts: response.data?.from_ts,
      to_ts: response.data?.to_ts,
      total_incidents: Number(response.data?.total_incidents || 0),
      escalated_total: Number(response.data?.escalated_total || 0),
      overdue_total: Number(response.data?.overdue_total || 0),
      scheduled_total: Number(response.data?.scheduled_total || 0),
      items: Array.isArray(response.data?.items)
        ? response.data.items.map((item: any) => ({
            escalation_state: String(item?.escalation_state || '').trim(),
            count: Number(item?.count || 0),
          }))
        : [],
      reason_items: Array.isArray(response.data?.reason_items)
        ? response.data.reason_items.map((item: any) => ({
            reason: String(item?.reason || '').trim(),
            count: Number(item?.count || 0),
          }))
        : [],
    };
  },

  /**
   * Delete incident
   */
  deleteIncident: async (incidentId: string): Promise<{ success: boolean }> => {
    await api.delete(`/api/v1/incidents/${incidentId}`);
    return { success: true };
  },

  /**
   * Quick create incident from alert
   */
  createFromAlert: async (
    alertTitle: string,
    alertDescription: string,
    severity: IncidentSeverity = 'high',
    zone?: string,
    cameraId?: string
  ): Promise<{ id: string; success: boolean }> => {
    return incidentsApi.createIncident({
      title: alertTitle,
      description: alertDescription,
      severity,
      incident_type: 'safety_alert',
      zone_id: zone,
      device_id: cameraId,
    });
  },

  // ============================================================================
  // ODOO INTEGRATION
  // ============================================================================

  /**
   * Get Odoo ticket status for an incident
   */
  getOdooStatus: async (incidentId: string): Promise<OdooStatus> => {
    const response = await api.get(`/api/v1/incidents/${incidentId}/odoo`);
    return response.data;
  },

  /**
   * Sync incident status from Odoo
   */
  syncFromOdoo: async (incidentId: string): Promise<{ success: boolean; state: string }> => {
    const response = await api.post(`/api/v1/incidents/${incidentId}/odoo/sync`);
    return {
      success: true,
      state: response.data.odoo_ticket_state,
    };
  },

  /**
   * Ensure an Odoo ticket exists for an incident (create if missing)
   */
  ensureOdooTicket: async (
    incidentId: string
  ): Promise<{ ok: boolean; incident_id?: string; odoo_ticket_id?: number; error?: string }> => {
    try {
      const response = await api.post(`/api/v1/incidents/${incidentId}/odoo/ensure`, {}, { timeout: 60000 });
      return response.data;
    } catch (err: any) {
      const data = err?.response?.data;
      if (data && typeof data === 'object') {
        return {
          ok: false,
          ...(data as Record<string, any>),
          error: String((data as any).error || err?.message || 'odoo_ensure_failed'),
        };
      }
      return { ok: false, error: String(err?.message || 'odoo_ensure_failed') };
    }
  },

  /**
   * Update Odoo ticket from Dashboard (two-way sync)
   */
  updateOdooTicket: async (
    incidentId: string,
    updates: { state?: string; severity?: string; assigned_to?: number }
  ): Promise<{ success: boolean }> => {
    await api.put(`/api/v1/incidents/${incidentId}/odoo/update`, updates);
    return { success: true };
  },

  /**
   * Bulk sync all incidents from Odoo
   */
  syncAllFromOdoo: async (): Promise<{ synced: number; failed: number; total: number }> => {
    const response = await api.post('/api/v1/incidents/odoo/sync-all');
    return response.data;
  },
};

export default incidentsApi;
